import { SessionManager } from "@mirage/manager/SessionManager";
import { IErrorMessage } from "@shared/mirage/core/contracts/IErrorMessage";
import { Transaction } from "@shared/mirage/core/contracts/Transaction";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { LocalDateFormat, convertDateTimeStringToObject } from "@shared/mirage/interceptors/response/convertDateTime";
import axios, { AxiosInstance, AxiosError } from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export abstract class BaseApiService {
    private _axios: AxiosInstance;
    private _prefix: string;
    private _serverUrl: string;
    private _urlsDoNotProlongSession = ["logout", "refreshSessionToken", "/last/"];
    constructor(prefix: string) {
        this._serverUrl = process.env.SERVER_URL || "";
        this._prefix = prefix;
        this._axios = axios.create({
            baseURL: this._serverUrl + "/api/" + this._prefix,
        });
        this._axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem("token");
                if (token && config && config.headers && config.headers.common) {
                    config.headers.common.set("Authorization", `Bearer ${token}`);
                }
                if (!this._urlsDoNotProlongSession.find((urlPart) => config.url?.includes(urlPart))) {
                    SessionManager.lastUserRequestTime = new Date();
                }
                config.data = DateFormatter.formatDatesInPayload(config.data);
                return config;
            },
            (error: AxiosError) => {
                return error.response;
            }
        );
        const datesConverter = convertDateTimeStringToObject(LocalDateFormat) as any;
        this._axios.interceptors.response.use(datesConverter, (error: AxiosError<IErrorMessage>) => {
            let message = "";
            if (error && error.response?.status) {
                if (error.response.status >= 500 && error.response.status < 600) {
                    message = "Server error, could not proceed with your request";
                    error.response.data = { errorMsg: message, is: "fail", error: { message } } as IErrorMessage & Transaction;
                    return error.response;
                } else if (error.response.status === 413) {
                    message = "The uploaded file is too large. Max size 12 MB";
                    error.response.data = { errorMsg: message, is: "fail", error: { message } } as IErrorMessage & Transaction;
                    return error.response;
                } else if (error.response.status === 403) {
                    message = "You don't have permissions to perform this action.";
                    error.response.data = { errorMsg: message, is: "fail", error: { message } } as IErrorMessage & Transaction;
                    return error.response;
                }
            }
            return error.response;
        });
    }

    public httpInstance(): AxiosInstance {
        return this._axios;
    }
}
